import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {RecipientStat} from '@gatewee/types/recipient-stat';
import moment from 'moment';

interface InvoiceByPayment {
  paymentMethod: string;
  totalAmount: number;
  percentage: number;
}

@Component({
  selector: 'gatewee-invoices-by-payment',
  templateUrl: './invoices-by-payment.component.html',
  styleUrls: ['./invoices-by-payment.component.css'],
  standalone: false,
})
export class InvoicesByPaymentComponent implements OnChanges {
  circumference = 2 * Math.PI * 40;

  @Input() stats: RecipientStat[] | null | undefined;

  invoices: InvoiceByPayment[] = [];
  private countsByMethod: Record<string, number> = {};

  availableMonths: Array<{index: number; label: string}> = [];
  currentIndex = 0;

  primaryColor = '';
  constructor() {
    this.primaryColor = getComputedStyle(
      document.documentElement,
    ).getPropertyValue('--primary');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stats']) {
      this.buildAvailableMonths();
      this.computeInvoices();
    }
  }

  get isLoading(): boolean {
    return this.stats === undefined;
  }

  get hasNoData(): boolean {
    const noStats =
      this.stats === null ||
      this.stats === undefined ||
      this.stats.length === 0;

    return !this.isLoading && (noStats || this.invoices.length === 0);
  }

  selectMonth(index: number): void {
    if (!this.stats || index < 0 || index >= this.stats.length) {
      return;
    }

    this.currentIndex = index;
    this.computeInvoices();
  }

  private buildAvailableMonths(): void {
    this.availableMonths = [];

    if (!this.stats || this.stats.length === 0) {
      this.currentIndex = 0;
      return;
    }

    const limit = Math.min(this.stats.length, 3);
    const options = this.stats.slice(0, limit).map((stat, idx) => ({
      index: idx,
      label: this.buildMonthReference(stat),
    }));

    this.availableMonths = options.reverse();

    if (this.currentIndex >= limit) {
      this.currentIndex = 0;
    }
  }

  private computeInvoices(): void {
    if (!this.stats || this.stats.length === 0) {
      this.invoices = [];
      this.countsByMethod = {};
      return;
    }

    if (this.currentIndex >= this.stats.length) {
      this.currentIndex = 0;
    }

    const selectedStat = this.stats[this.currentIndex] ?? this.stats[0];
    if (!selectedStat) {
      this.invoices = [];
      this.countsByMethod = {};
      return;
    }

    const volumeByMethod = selectedStat.invoiceVolumeByMethod ?? {};
    this.countsByMethod = selectedStat.invoiceCountByMethod ?? {};

    const keys = Object.keys(volumeByMethod);
    if (keys.length === 0) {
      this.invoices = [];
      return;
    }

    const totalVolume = keys.reduce(
      (sum, method) => sum + (volumeByMethod[method] ?? 0),
      0,
    );

    if (totalVolume === 0) {
      this.invoices = [];
      return;
    }

    this.invoices = keys
      .map(method => ({
        paymentMethod: method,
        totalAmount: volumeByMethod[method] ?? 0,
        percentage: Math.round(
          ((volumeByMethod[method] ?? 0) / totalVolume) * 100,
        ),
      }))
      .filter(invoice => invoice.totalAmount > 0)
      .sort((a, b) => b.totalAmount - a.totalAmount);
  }

  calculateOffset(percentage: number): number {
    return this.circumference * (1 - percentage / 100);
  }

  getCountFor(method: string): number {
    return this.countsByMethod[method] ?? 0;
  }

  private buildMonthReference(stat: RecipientStat): string {
    const date = stat.periodDate
      ? moment(stat.periodDate)
      : moment({
          year: stat.year,
          month: (stat.month ?? 1) - 1,
          day: 1,
        });

    if (!date.isValid()) {
      const year = stat.year ?? new Date().getFullYear();
      const month = (stat.month ?? 1) - 1;
      return moment({year, month, day: 1}).format('YYYY-MM-01');
    }

    return date.startOf('month').format('YYYY-MM-01');
  }
}
